// src/hooks/useGroupHome.js
//
// Shared Group-home data hook. Lifted out of SidebarGroups (June 2026, Groups Tier 1)
// so the desktop Group-home Mode Column — and, later, the mobile drawer — drive the same
// membership + hierarchy orchestration the same way: one source of truth.
//
// This is a RELOCATION of orchestration, not a rewrite. Every groupService.* and
// groupHelpers.* call is identical to the one SidebarGroups made; the membership state
// machine (owner / member / pending / visitor) moves across intact. What did NOT come over:
// the sidebar's mode-headline preview list (presentation that dies with SidebarGroups) and
// its emoji debug console.logs. Effects gained `cancelled` guards to match house style
// (useDirectoryListings) — guards only; no call or branch changed.
//
// Consuming components own their own modal/toast RENDERING; the hook owns the open/close
// state and the actions, so the flow (approval modal → handleJoinGroup) stays in one place.

import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../i18n/useTranslation';
import { groupService } from '../data/dataService';
import { supabaseFetch } from '../lib/supabase';
import { getPublicProfiles } from '../data/profileService';
import { getChildGroups, getFederationMembers, checkBatchMembership, getGroupHierarchy, getGroupShareUrl } from '../utils/groupHelpers';
import { rateLimitMessage } from '../utils/rateLimitMessage';

// showMembers (default false): privacy-by-construction. The members roster is NOT fetched or
// exposed unless a consumer opts in. Until the deferred owner-controlled communities.show_members
// flag ships, no surface passes true — so the roster fetch never runs. Membership ACTIONS
// (join/leave) and the aggregate member_count are unaffected; only the per-member roster is gated.
export function useGroupHome(group, currentUser, { onMembershipChange, showMembers = false } = {}) {
  const { requireAuth, isLoggedIn } = useAuth();
  // Toast strings (join/leave/request) are translated via the shared 'ui' namespace keys the
  // mobile drawer already defined; the `|| English` fallback preserves the prior copy if a key
  // is absent. This closes the desktop front page's latent English-only-toast gap.
  const { t } = useTranslation('ui');

  // Only use userId if actually logged in (no test-user fallback for membership checks)
  const userId = currentUser?.id;

  // Membership state machine
  const [membershipStatus, setMembershipStatus] = useState({
    isMember: false,
    role: null,
    isOwner: false,
    hasPendingRequest: false,
    loading: true
  });

  // Hierarchy state
  const [childGroups, setChildGroups] = useState([]);
  const [childMemberships, setChildMemberships] = useState({});
  const [federationMembers, setFederationMembers] = useState([]);
  const [federationMemberships, setFederationMemberships] = useState({});
  const [parentGroup, setParentGroup] = useState(null);
  const [federationGroup, setFederationGroup] = useState(null);

  // Owner display name — fetched locally so it works regardless of how the group was selected
  const [ownerDisplayName, setOwnerDisplayName] = useState(null);

  // Members preview list (avatars + role) — added Tier 1 Commit 2; SidebarGroups had no
  // members list, so this is the one genuinely-new fetch on top of the relocation.
  const [members, setMembers] = useState([]);

  // Whether a PUBLISHED landing page exists for this group (Tier 2). Drives the mobile Home
  // tab's "read the full intro" link — shown only when there's a long-form page to open to.
  // Cheapest reliable signal: a single keyed lookup on community_landing (one row per
  // community — community_id is UNIQUE) selecting is_published. Same probe MainFeed's §4
  // gate uses (hasLanding), here relocated into the shared hook so any home surface can read it.
  const [hasLandingPage, setHasLandingPage] = useState(false);

  // UI flags (the consuming column renders the modals/toast from these)
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [joinLoading, setJoinLoading] = useState(false);
  const [joinMessage, setJoinMessage] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  // Federation 4B — can the viewer apply to federate with THIS group (a distinct question
  // from their own membership in it: the applicant is one of the viewer's OTHER groups).
  // eligibleGroups: structurally eligible (admin, top-level, not already federated), each
  // carrying hasPendingRequest for this exact target. applicableGroups excludes the ones
  // already pending — that's the multi-group choice offered when the Apply control is used.
  const [federationApplyEligibility, setFederationApplyEligibility] = useState({
    loading: true,
    eligibleGroups: [],
    applicableGroups: []
  });
  const [showFederationApplyModal, setShowFederationApplyModal] = useState(false);
  const [federationApplyLoading, setFederationApplyLoading] = useState(false);
  const [federationApplyMessage, setFederationApplyMessage] = useState('');
  // Separate from the message text itself — the message is translated (pt-PT included), so
  // styling can't string-match an English substring the way the pre-existing joinMessage does.
  const [federationApplySuccess, setFederationApplySuccess] = useState(false);

  // Member-facing "Share this group" — the resolver seam; today only public groups resolve to
  // a shareable URL (see getGroupShareUrl's own doc comment for why).
  const shareInfo = getGroupShareUrl(group);

  // --- Complete group row ---------------------------------------------------
  // A card-sourced `group` (Groups I Own, Joined, Discover, a sub-group row) carries whatever
  // narrow `select` list that card's list query used — never widen those queries to chase every
  // field a group-page section might need (that just moves the same "next missing field"
  // problem into the card query). Instead: the group PAGE holds the full row. Seed synchronously
  // with whatever arrived (no flash/regression when it's already complete), then upgrade to a
  // full fetch only when it's narrow. `is_federation` is a non-null boolean column
  // (DATABASE.md), so `undefined` here can only mean a `select` omitted it — never a real
  // fetched value — making it a reliable (if bug-specific, not general-purpose) completeness
  // signal. GroupHomeColumn/MobileGroupFrontPage must render GroupHomePanel/GroupIdentityCard
  // from THIS value, not from the raw `group` prop they were handed.
  const [completeGroup, setCompleteGroup] = useState(group);

  useEffect(() => {
    let cancelled = false;

    setCompleteGroup(group);

    if (!group?.id || group.is_federation !== undefined) {
      return;
    }

    supabaseFetch.query('communities', {
      select: '*',
      eq: { id: group.id },
      single: true
    }).then(({ data, error }) => {
      if (cancelled) return;
      if (!error && data) {
        setCompleteGroup({ ...group, ...data });
      }
    });

    return () => { cancelled = true; };
  }, [group?.id, group?.is_federation]);

  // --- Owner display name -------------------------------------------------
  useEffect(() => {
    // Use pre-fetched value if available, otherwise fetch it
    if (group?.owner_display_name) {
      setOwnerDisplayName(group.owner_display_name);
      return;
    }
    if (!group?.owner_id) {
      setOwnerDisplayName(null);
      return;
    }
    let cancelled = false;
    getPublicProfiles([group.owner_id]).then(({ data }) => {
      if (!cancelled) setOwnerDisplayName(data?.[0]?.display_name || null);
    });
    return () => { cancelled = true; };
  }, [group?.id, group?.owner_id, group?.owner_display_name]);

  // --- Hierarchy: children, federation members, parent --------------------
  useEffect(() => {
    let cancelled = false;

    const loadGroupHierarchyData = async () => {
      if (!group?.id) {
        setChildGroups([]);
        setChildMemberships({});
        setFederationMembers([]);
        setFederationMemberships({});
        setParentGroup(null);
        setFederationGroup(null);
        return;
      }

      try {
        // Load children
        const children = await getChildGroups(group.id);

        // Check membership in ALL children first (before filtering)
        let memberships = {};
        if (userId && children && children.length > 0) {
          memberships = await checkBatchMembership(userId, children.map(c => c.id));
        }

        // Filter: show children where user is a member OR visible_to_parent !== false (public).
        // Private children (visible_to_parent: false) only show if the user is a member.
        const visible = (children || []).filter(c => {
          const isMember = !!memberships[c.id];
          const isPublic = c.visible_to_parent !== false;
          return isMember || isPublic;
        });

        // Batch-fetch owner display names for all visible children
        const ownerIds = [...new Set(visible.map(c => c.owner_id).filter(Boolean))];
        let ownerMap = {};
        if (ownerIds.length > 0) {
          const { data: owners } = await getPublicProfiles(ownerIds);
          if (owners) {
            owners.forEach(o => { ownerMap[o.id] = o.display_name; });
          }
        }
        const visibleWithOwners = visible.map(c => ({
          ...c,
          owner_display_name: ownerMap[c.owner_id] || null
        }));

        if (cancelled) return;
        setChildGroups(visibleWithOwners);
        setChildMemberships(memberships);

        // Load federation members if this is a federation group. Reads completeGroup, not the
        // raw `group` argument — a card-sourced group may not have selected is_federation at
        // all (see the completeGroup effect above); gating on the possibly-narrow argument
        // here would leave this section empty even after completeGroup resolves.
        //
        // No visible_to_parent filter here (Michael's ruling, following CC's probe, 12 Sep):
        // that flag is a nested-hierarchy concept — whether a CHILD shows in its PARENT's
        // sub-groups list — and has no defined meaning for a federation member, whose
        // parent_group_id is NULL by definition. Copy-pasting the sub-groups filter (below,
        // unchanged) onto federation members silently emptied this section for any member
        // whose visible_to_parent happened to be false, which it is for demo/legacy rows never
        // set with federation in mind. Federation members are listed because they are
        // federation members — soft-delete filtering (getFederationMembers' own
        // deleted_at IS NULL) still applies; nothing else gates the list.
        if (completeGroup?.is_federation) {
          const fedMembers = await getFederationMembers(group.id);

          // Fetch owner names for federation members
          const fedOwnerIds = [...new Set((fedMembers || []).map(m => m.owner_id).filter(Boolean))];
          let fedOwnerMap = {};
          if (fedOwnerIds.length > 0) {
            const { data: fedOwners } = await getPublicProfiles(fedOwnerIds);
            if (fedOwners) {
              fedOwners.forEach(o => { fedOwnerMap[o.id] = o.display_name; });
            }
          }
          const fedMembersWithOwners = (fedMembers || []).map(m => ({
            ...m,
            owner_display_name: fedOwnerMap[m.owner_id] || null
          }));

          if (cancelled) return;
          setFederationMembers(fedMembersWithOwners);
          setFederationMemberships({});
        } else {
          setFederationMembers([]);
          setFederationMemberships({});
        }

        // Load parent if this is a sub-group — same reasoning: read completeGroup's
        // parent_group_id, not the raw argument's, for the same narrow-projection reason.
        if (completeGroup?.parent_group_id) {
          const hierarchy = await getGroupHierarchy(group.id);
          // hierarchy is ordered root → current, so parent is second-to-last
          if (hierarchy && hierarchy.length > 1) {
            const minimalParent = hierarchy[hierarchy.length - 2];
            // Hierarchy only returns {id, name, hierarchy_level, depth} — fetch full record for images + owner
            const { data: fullParent } = await supabaseFetch.query('communities', {
              select: 'id, name, description, logo_url, cover_image_url, community_type, parent_group_id, hierarchy_level, owner_id, is_private, auto_accept_members, member_count, article_count',
              eq: { id: minimalParent.id },
              single: true
            });
            if (fullParent?.owner_id) {
              const { data: ownerProfiles } = await getPublicProfiles([fullParent.owner_id]);
              fullParent.owner_display_name = ownerProfiles?.[0]?.display_name || null;
            }
            if (!cancelled) setParentGroup(fullParent || minimalParent);
          } else {
            if (!cancelled) setParentGroup(null);
          }
        } else {
          if (!cancelled) setParentGroup(null);
        }

        // Load the federation this group belongs to (4D) — the reverse of the federation-members
        // block above: a MEMBER resolving UP to its federation, not a federation resolving down
        // to its members. Reads completeGroup.federation_id, same narrow-projection reasoning as
        // is_federation/parent_group_id above. No RPC needed here — unlike get_group_hierarchy,
        // this isn't a recursive walk, just a single-row lookup, so it takes the same direct-query
        // shape as the parent fetch two blocks up rather than a new function. is_federation +
        // federation_id are included in the select so a click-through doesn't immediately re-fire
        // the completeGroup self-heal fetch above for a field this fetch already had for free.
        // `is: { deleted_at: null }` is the ratified federation soft-delete rule (filter on read,
        // never clear federation_id on write) — RLS's owner-exception clause would otherwise let
        // an owner see their own soft-deleted federation here even though the public read policy
        // wouldn't.
        if (completeGroup?.federation_id) {
          const { data: federation } = await supabaseFetch.query('communities', {
            select: 'id, name, description, logo_url, cover_image_url, community_type, is_federation, federation_id, hierarchy_level, owner_id, is_private, auto_accept_members, member_count, article_count',
            eq: { id: completeGroup.federation_id },
            is: { deleted_at: null },
            single: true
          });
          if (!cancelled) setFederationGroup(federation || null);
        } else {
          if (!cancelled) setFederationGroup(null);
        }
      } catch (error) {
        console.error('Error loading group hierarchy data:', error);
        if (cancelled) return;
        setChildGroups([]);
        setChildMemberships({});
        setFederationMembers([]);
        setFederationMemberships({});
        setParentGroup(null);
        setFederationGroup(null);
      }
    };

    loadGroupHierarchyData();

    return () => { cancelled = true; };
  }, [group?.id, userId, completeGroup?.is_federation, completeGroup?.parent_group_id, completeGroup?.federation_id]);

  // --- Members preview list -----------------------------------------------
  // Owner first, then admins, then the rest. The total count comes from the group's
  // denormalised member_count; this list is a capped preview for the rail avatars.
  useEffect(() => {
    let cancelled = false;

    const loadMembers = async () => {
      // Gated: skip the roster fetch entirely unless a consumer opts in via showMembers.
      if (!showMembers || !group?.id) {
        setMembers([]);
        return;
      }
      try {
        const { data: rows } = await supabaseFetch.query('community_members', {
          select: 'user_id, role',
          eq: { community_id: group.id },
          limit: 24
        });
        if (!rows || rows.length === 0) {
          if (!cancelled) setMembers([]);
          return;
        }

        const ids = [...new Set(rows.map(r => r.user_id).filter(Boolean))];
        const { data: users } = await getPublicProfiles(ids);
        const userMap = {};
        (users || []).forEach(u => { userMap[u.id] = u; });

        const rank = (m) => (m.id === group.owner_id ? 0 : (m.role === 'admin' ? 1 : 2));
        const list = rows
          .map(r => ({
            id: r.user_id,
            role: r.role,
            display_name: userMap[r.user_id]?.display_name || null,
            avatar_url: userMap[r.user_id]?.avatar_url || null
          }))
          .sort((a, b) => rank(a) - rank(b));

        if (!cancelled) setMembers(list);
      } catch (error) {
        console.error('Error loading group members:', error);
        if (!cancelled) setMembers([]);
      }
    };

    loadMembers();

    return () => { cancelled = true; };
  }, [group?.id, group?.owner_id, showMembers]);

  // --- Published landing page existence (for the "read full intro" link) ---
  useEffect(() => {
    let cancelled = false;
    if (!group?.id) {
      setHasLandingPage(false);
      return;
    }
    supabaseFetch.query('community_landing', {
      select: 'is_published',
      eq: { community_id: group.id, is_published: true },
      single: true
    }).then(({ data }) => {
      if (!cancelled) setHasLandingPage(!!data?.is_published);
    }).catch(() => {
      if (!cancelled) setHasLandingPage(false);
    });
    return () => { cancelled = true; };
  }, [group?.id]);

  // --- Membership status (only when logged in) ----------------------------
  useEffect(() => {
    let cancelled = false;

    const checkMembershipStatus = async () => {
      if (!group?.id) {
        setMembershipStatus({ isMember: false, role: null, isOwner: false, hasPendingRequest: false, loading: false });
        return;
      }

      // If not logged in, skip membership check — user is a visitor
      if (!isLoggedIn || !userId) {
        setMembershipStatus({ isMember: false, role: null, isOwner: false, hasPendingRequest: false, loading: false });
        return;
      }

      setMembershipStatus(prev => ({ ...prev, loading: true }));

      try {
        // Check if user is owner
        const isOwner = group.owner_id === userId;

        // Check membership
        const { isMember, role } = await groupService.checkMembership(userId, group.id);

        // Check pending request
        const pendingRequest = await groupService.getJoinRequestStatus(userId, group.id);
        const hasPendingRequest = pendingRequest?.status === 'pending';

        if (cancelled) return;
        setMembershipStatus({
          isMember: isMember || isOwner,
          role: isOwner ? 'owner' : role,
          isOwner,
          hasPendingRequest,
          loading: false
        });
      } catch (error) {
        console.error('Error checking membership:', error);
        if (!cancelled) {
          setMembershipStatus({ isMember: false, role: null, isOwner: false, hasPendingRequest: false, loading: false });
        }
      }
    };

    checkMembershipStatus();

    return () => { cancelled = true; };
  }, [group?.id, userId, isLoggedIn]);

  // --- Federation-apply eligibility (Federation 4B §1) ---------------------
  // Only meaningful when THIS group is a federation and the viewer is logged in — the
  // question is whether one of the viewer's OTHER groups can apply, not the viewer's own
  // membership here (that's the state machine above).
  useEffect(() => {
    let cancelled = false;

    const loadFederationEligibility = async () => {
      // Reads completeGroup, not the raw `group` argument — see the completeGroup effect above.
      if (!group?.id || !completeGroup?.is_federation || !userId) {
        setFederationApplyEligibility({ loading: false, eligibleGroups: [], applicableGroups: [] });
        return;
      }
      try {
        const groups = await groupService.getFederationEligibleApplicantGroups(userId, group.id);
        if (cancelled) return;
        setFederationApplyEligibility({
          loading: false,
          eligibleGroups: groups,
          applicableGroups: groups.filter(g => !g.hasPendingRequest)
        });
      } catch (error) {
        console.error('Error loading federation apply eligibility:', error);
        if (!cancelled) setFederationApplyEligibility({ loading: false, eligibleGroups: [], applicableGroups: [] });
      }
    };

    loadFederationEligibility();

    return () => { cancelled = true; };
  }, [group?.id, completeGroup?.is_federation, userId]);

  // --- Actions ------------------------------------------------------------

  // Join button click — approval groups open the optional-message modal; auto-accept joins directly
  const handleJoinClick = () => {
    requireAuth('gate_body_join_group', () => {
      if (group?.auto_accept_members === false) {
        setShowJoinModal(true);
      } else {
        handleJoinGroup(null);
      }
    });
  };

  const handleJoinGroup = async (message) => {
    setJoinLoading(true);
    try {
      const result = await groupService.joinGroup(userId, group.id, message);

      if (result.success) {
        if (result.status === 'joined') {
          // Immediate join — update local state
          setMembershipStatus(prev => ({ ...prev, isMember: true, role: 'member' }));
          setJoinMessage(t('joined_group_success', { name: result.groupName }) || `You've joined ${result.groupName}!`);
          // Notify the consumer to refresh content
          if (onMembershipChange) onMembershipChange();
        } else if (result.status === 'pending') {
          // Request submitted
          setMembershipStatus(prev => ({ ...prev, hasPendingRequest: true }));
          setJoinMessage(t('join_request_submitted') || `Your request to join ${result.groupName} has been submitted. The group owner will review it.`);
        }
      } else if (result.status === 'blocked') {
        setJoinMessage(t('groups:cannot_request_to_join'));
      } else if (result.status === 'rate_limited') {
        setJoinMessage(rateLimitMessage(result.error, t) || t('common:rate_limit_generic'));
      } else {
        setJoinMessage(result.message || 'Failed to join group');
      }
    } catch (error) {
      console.error('Error joining group:', error);
      setJoinMessage('An error occurred. Please try again.');
    } finally {
      setJoinLoading(false);
      setShowJoinModal(false);
      setTimeout(() => setJoinMessage(''), 3000);
    }
  };

  const handleCancelRequest = async () => {
    setJoinLoading(true);
    try {
      const success = await groupService.cancelJoinRequest(userId, group.id);
      if (success) {
        setMembershipStatus(prev => ({ ...prev, hasPendingRequest: false }));
        setJoinMessage(t('join_request_cancelled') || 'Join request cancelled');
        setTimeout(() => setJoinMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error canceling request:', error);
    } finally {
      setJoinLoading(false);
    }
  };

  // Federation-apply click — opens the group-picker/form modal (auth-gated, mirrors handleJoinClick)
  const handleFederationApplyClick = () => {
    requireAuth('gate_body_apply_federation', () => {
      setShowFederationApplyModal(true);
    });
  };

  const submitFederationApplication = async (applicantGroupId, requestData) => {
    setFederationApplyLoading(true);
    try {
      const result = await groupService.applyToFederation(userId, applicantGroupId, group.id, requestData);

      if (result.success) {
        // Move this applicant group from applicable to pending locally — no need to re-fetch.
        setFederationApplyEligibility(prev => ({
          ...prev,
          eligibleGroups: prev.eligibleGroups.map(g =>
            g.id === applicantGroupId ? { ...g, hasPendingRequest: true } : g
          ),
          applicableGroups: prev.applicableGroups.filter(g => g.id !== applicantGroupId)
        }));
        setFederationApplySuccess(true);
        setFederationApplyMessage(t('federation_application_submitted') || 'Application submitted.');
        setShowFederationApplyModal(false);
      } else if (result.status === 'pending') {
        setFederationApplySuccess(false);
        setFederationApplyMessage(t('federation_application_already_pending') || 'A request is already pending for this group.');
      } else if (result.status === 'blocked') {
        setFederationApplySuccess(false);
        setFederationApplyMessage(result.message);
      } else {
        setFederationApplySuccess(false);
        setFederationApplyMessage(result.message || 'Failed to submit application.');
      }
      return result;
    } finally {
      setFederationApplyLoading(false);
      setTimeout(() => setFederationApplyMessage(''), 3000);
    }
  };

  // Leave — show confirmation first, then confirm
  const handleLeaveClick = () => {
    setShowLeaveConfirmation(true);
  };

  const confirmLeaveGroup = async () => {
    setShowLeaveConfirmation(false);
    setJoinLoading(true);
    try {
      const success = await groupService.leaveGroup(userId, group.id);
      if (success) {
        setMembershipStatus(prev => ({ ...prev, isMember: false, role: null }));
        setJoinMessage(t('left_group_success', { name: group.name }) || `You have left ${group.name}`);
        if (onMembershipChange) onMembershipChange();
        setTimeout(() => setJoinMessage(''), 3000);
      } else {
        setJoinMessage('Failed to leave group');
        setTimeout(() => setJoinMessage(''), 3000);
      }
    } catch (error) {
      console.error('Error leaving group:', error);
      setJoinMessage('An error occurred. Please try again.');
      setTimeout(() => setJoinMessage(''), 3000);
    } finally {
      setJoinLoading(false);
    }
  };

  // Member-facing Share — a plain link to the group's public surface, no auto-join semantics
  // (deliberately not smartLinkService/ShareLinkModal, per the build brief). Tries the platform
  // share sheet first, matching ShareLinkModal's own handleNativeShare/handleCopyLink pattern;
  // falls back to clipboard copy with a 2-second "copied" flag for the button label.
  const handleShareGroup = async () => {
    if (!shareInfo.isShareable) return;
    const { shareUrl } = shareInfo;

    if (navigator.share) {
      try {
        await navigator.share({ title: group.name, text: group.name, url: shareUrl });
        return;
      } catch (error) {
        if (error.name === 'AbortError') return; // user cancelled — no fallback
        // fall through to clipboard copy
      }
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    } catch (error) {
      console.error('Error copying share link:', error);
    }
  };

  // Refresh child / federation memberships after a join action on a card
  const refreshChildMemberships = async () => {
    if (userId && childGroups.length > 0) {
      const memberships = await checkBatchMembership(userId, childGroups.map(c => c.id));
      setChildMemberships(memberships);
    }
  };

  const refreshFederationMemberships = async () => {
    if (userId && federationMembers.length > 0) {
      const memberships = await checkBatchMembership(userId, federationMembers.map(m => m.id));
      setFederationMemberships(memberships);
    }
  };

  return {
    // auth passthrough
    isLoggedIn,
    // The full communities row — render the group page's identity/federation/hierarchy
    // sections off THIS, not the raw `group` argument, which may be a card's narrow projection.
    completeGroup,
    // membership state machine
    membershipStatus,
    // hierarchy data
    childGroups,
    childMemberships,
    federationMembers,
    federationMemberships,
    parentGroup,
    federationGroup,
    ownerDisplayName,
    members,
    hasLandingPage,
    // actions
    handleJoinClick,
    handleJoinGroup,
    handleCancelRequest,
    handleLeaveClick,
    confirmLeaveGroup,
    refreshChildMemberships,
    refreshFederationMemberships,
    // Federation 4B — apply-to-federate eligibility, action, and modal flags
    federationApplyEligibility,
    handleFederationApplyClick,
    submitFederationApplication,
    showFederationApplyModal,
    setShowFederationApplyModal,
    federationApplyLoading,
    federationApplyMessage,
    federationApplySuccess,
    // UI flags for modals/toast (rendered by the consuming column)
    showJoinModal,
    setShowJoinModal,
    joinLoading,
    joinMessage,
    showShareModal,
    setShowShareModal,
    showLeaveConfirmation,
    setShowLeaveConfirmation,
    // member-facing Share (resolver seam + action)
    shareInfo,
    shareCopied,
    handleShareGroup
  };
}

export default useGroupHome;
